import React, { useMemo, useState } from "react";
import {
  Card,
  Col,
  DatePicker,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Empty,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  BookItem,
  DailyBookingRow,
  DailySummary,
  HOURLY_RATE,
  SalaryPeriodEndDay,
  UserSummary,
  buildPeriodEmployeeReport,
  // calculateUserReport,
} from "utils/employeeReport";
const { Title, Text } = Typography;

interface Props {
  books: BookItem[];
}

const currency = (value: number) => `₮${value.toLocaleString()}`;
const hoursText = (value: number) => `${value} цаг`;

const EmployeeReportDashboard: React.FC<Props> = ({ books }) => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [periodEndDay, setPeriodEndDay] = useState<SalaryPeriodEndDay>(10);
  const report = useMemo(() => {
    if (!books.length) {
      return {
        startDate: dayjs(),
        endDate: dayjs(),
        userSummary: [],
        dailySummary: [],
        totalBookings: 0,
        totalHours: 0,
        totalSalary: 0,
      };
    }

    return buildPeriodEmployeeReport(
      books,
      [],
      selectedMonth,
      periodEndDay,
      HOURLY_RATE
    );
  }, [books, selectedMonth, periodEndDay]);

  const userColumns: ColumnsType<UserSummary> = [
    {
      title: "Ажилтан",
      dataIndex: "user",
      key: "user",
      width: 160,
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: "Booking",
      dataIndex: "bookings",
      key: "bookings",
      width: 110,
      align: "center",
    },
    {
      title: "Ажилласан цаг",
      dataIndex: "workedHours",
      key: "workedHours",
      width: 150,
      align: "center",
      render: (value: number) => hoursText(value),
    },
    {
      title: "Цагийн хөлс",
      dataIndex: "hourlyRate",
      key: "hourlyRate",
      width: 140,
      align: "center",
      render: (value: number) => currency(value),
    },
    {
      title: "Тооцсон цалин",
      dataIndex: "salary",
      key: "salary",
      width: 180,
      align: "right",
      render: (value: number) => <Text strong>{currency(value)}</Text>,
    },
  ];

  const bookingDetailColumns: ColumnsType<DailyBookingRow> = [
    {
      title: "Огноо",
      dataIndex: "fullDate",
      key: "fullDate",
      width: 120,
    },
    {
      title: "Эхлэх",
      dataIndex: "bookedTime",
      key: "bookedTime",
      width: 100,
    },
    {
      title: "Дуусах",
      key: "endDateTime",
      width: 100,
      render: (_, record) => dayjs(record.endDateTime).format("HH:mm"),
    },
    {
      title: "Ажилтан",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 120,
      render: (value: string) => <Tag>{value}</Tag>,
    },
    {
      title: "Багц",
      dataIndex: "packageName",
      key: "packageName",
    },
    {
      title: "Үргэлжлэх хугацаа",
      key: "durationMinutes",
      width: 150,
      align: "center",
      render: (_, record) => `${record.durationMinutes} мин`,
    },
    {
      title: "Session цаг",
      key: "workedHours",
      render: (_, record) =>
        hoursText(Number((record.durationMinutes / 60).toFixed(2))),
    },
  ];
  const dailyColumns: ColumnsType<DailySummary> = [
    {
      title: "Огноо",
      dataIndex: "date",
      key: "date",
      width: 140,
      render: (value: string) => dayjs(value).format("YYYY-MM-DD ddd"),
    },
    {
      title: "Booking",
      dataIndex: "totalBookings",
      key: "totalBookings",
      width: 110,
      align: "center",
    },
    {
      title: "Нийт цаг",
      dataIndex: "totalWorkedHours",
      key: "totalWorkedHours",
      width: 130,
      align: "center",
      render: (value: number) => hoursText(value),
    },
    {
      title: "Нийт цалин",
      dataIndex: "totalSalary",
      key: "totalSalary",
      width: 160,
      align: "right",
      render: (value: number) => <Text strong>{currency(value)}</Text>,
    },
    {
      title: "Ажилтнууд",
      key: "users",
      render: (_, record) => (
        <Space size={[8, 8]} wrap>
          {record.users.map((user) => (
            <Tag key={`${record.date}-${user.user}`}>
              {user.user}: {user.workedHours}ц / {currency(user.salary)}
            </Tag>
          ))}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Card
          styles={{
            body: {
              padding: 20,
            },
          }}
        >
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={16}>
              <Space direction="vertical" size={4}>
                <Title level={3} style={{ margin: 0 }}>
                  Employee Report Dashboard
                </Title>
                <Text type="secondary">
                  Цалингийн тайлан 10, 20, 30-ны үеэр бодогдоно
                </Text>
              </Space>
            </Col>

            <Col xs={24} md={8} style={{ textAlign: "right" }}>
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <Text strong>Сар сонгох</Text>
                <DatePicker
                  picker="month"
                  value={selectedMonth}
                  onChange={(value) => value && setSelectedMonth(value)}
                  allowClear={false}
                  style={{ width: 260 }}
                />
                <Segmented
                  value={periodEndDay}
                  onChange={(value) =>
                    setPeriodEndDay(Number(value) as SalaryPeriodEndDay)
                  }
                  options={[
                    { label: "1-10", value: 10 },
                    { label: "11-20", value: 20 },
                    { label: "21-с сарын сүүл", value: 30 },
                  ]}
                  block
                  style={{ width: 260 }}
                />
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Цалингийн үе"
                value={`${report.startDate.format(
                  "MM-DD"
                )} ~ ${report.endDate.format("MM-DD")}`}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic title="Нийт booking" value={report.totalBookings} />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Нийт ажилласан цаг"
                value={report.totalHours}
                suffix="цаг"
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Нийт тооцсон цалин"
                value={report.totalSalary}
                formatter={(value) => currency(Number(value))}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Ажилтнаар нэгтгэсэн тайлан">
          <Table<UserSummary>
            rowKey="key"
            columns={userColumns}
            dataSource={report.userSummary}
            pagination={false}
            locale={{
              emptyText: <Empty description="Тухайн үед мэдээлэл алга" />,
            }}
            expandable={{
              expandedRowRender: (record) => (
                <Table<DailyBookingRow>
                  rowKey="_id"
                  columns={bookingDetailColumns}
                  dataSource={record.bookingList}
                  pagination={false}
                  size="small"
                />
              ),
            }}
            scroll={{ x: 900 }}
          />
        </Card>

        <Card title="Өдрөөр задласан тайлан">
          <Table<DailySummary>
            rowKey="key"
            columns={dailyColumns}
            dataSource={report.dailySummary}
            pagination={false}
            locale={{
              emptyText: <Empty description="Тухайн үед мэдээлэл алга" />,
            }}
            expandable={{
              expandedRowRender: (record) => (
                <Table<DailyBookingRow>
                  rowKey="_id"
                  columns={bookingDetailColumns}
                  dataSource={record.bookingList}
                  pagination={false}
                  size="small"
                />
              ),
            }}
            scroll={{ x: 1000 }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default EmployeeReportDashboard;
